import { ChangeEvent, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { cancelExpressDocument } from "../../services/expressDocument";
import { localizeApiMessage } from "../../errors/messages";
import { apiErrorMessage } from "../SpecialOrder/data";
import { DOCUMENT_TEXT_MAX_LENGTH } from "./constants";

interface CancelDocumentDialogProps {
  open: boolean;
  documentUuid: string;
  docNo: string;
  onClose: () => void;
  onCancelled: () => void;
}

const FALLBACK_ERROR = "ยกเลิกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";

/**
 * Cancel a document with a reason. A cancelled document stays on record and can no longer be
 * edited; for a receipt, its payments stop counting against the bills it paid. A refusal is shown
 * inside the dialog, not as a toast behind it.
 */
export default function CancelDocumentDialog({
  open,
  documentUuid,
  docNo,
  onClose,
  onCancelled,
}: Readonly<CancelDocumentDialogProps>) {
  const [reason, setReason] = useState<string>("");
  const [reasonError, setReasonError] = useState<string>("");
  const [requestError, setRequestError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setReasonError("");
      setRequestError("");
    }
  }, [open]);

  const handleReasonChange = (event: ChangeEvent<HTMLInputElement>) => {
    setReason(event.target.value);
    setReasonError("");
  };

  const handleConfirm = async () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setReasonError("กรุณาระบุเหตุผลการยกเลิก");
      return;
    }
    setLoading(true);
    setRequestError("");
    try {
      await cancelExpressDocument(documentUuid, { reason: trimmedReason });
      onCancelled();
    } catch (error) {
      const message = apiErrorMessage(error);
      setRequestError(message ? localizeApiMessage(message) : FALLBACK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>ยกเลิกเอกสาร</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" mb={2}>
          ยกเลิกเอกสาร {docNo} แล้วแก้ไขไม่ได้ ถ้าต้องการแก้ไขให้บันทึกเอกสารใหม่
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label="เหตุผลการยกเลิก"
          required
          value={reason}
          onChange={handleReasonChange}
          error={Boolean(reasonError)}
          helperText={reasonError}
          slotProps={{ htmlInput: { maxLength: DOCUMENT_TEXT_MAX_LENGTH } }}
        />
        {requestError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {requestError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          ปิด
        </Button>
        <Button variant="contained" color="error" loading={loading} onClick={handleConfirm}>
          ยืนยันยกเลิก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
